import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Check, Loader2, Send } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api } from "@/lib/api";

/* Formulaire d'inscription newsletter — collecte uniquement (aucune campagne envoyée
   pour l'instant). Consentement explicite : case NON précochée, lien vers la politique
   de confidentialité. Variantes : clair (modal) / sombre (footer). */
export const NewsletterForm = ({ source = "opportunites", dark = false, compact = false }) => {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [hp, setHp] = useState(""); // honeypot
  const [state, setState] = useState("idle"); // idle | sending | ok | already
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (state === "sending") return;
    setError("");
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) return setError(t("newsletter.errors.email"));
    if (!consent) return setError(t("newsletter.errors.consent"));
    setState("sending");
    try {
      const { data } = await api.post("/newsletter/subscribe", {
        email: email.trim(),
        lang: i18n.language?.startsWith("en") ? "en" : "fr",
        source,
        consent,
        website: hp,
      });
      setState(data.already ? "already" : "ok");
    } catch (err) {
      setState("idle");
      setError(err.response?.status === 429 ? t("newsletter.errors.rate") : t("newsletter.errors.generic"));
    }
  };

  if (state === "ok" || state === "already") {
    return (
      <div
        className={`flex items-start gap-2.5 rounded-xl px-4 py-3.5 text-sm ${dark ? "bg-white/10 text-white/90" : "bg-[#2F9E44]/8 text-[#1E7A34]"}`}
        data-testid={`newsletter-success-${source}`}
      >
        <Check className={`mt-0.5 h-4 w-4 shrink-0 ${dark ? "text-[#4DB8FF]" : "text-[#2F9E44]"}`} />
        <span>{state === "already" ? t("newsletter.already") : t("newsletter.success")}</span>
      </div>
    );
  }

  const inputCls = dark
    ? "w-full rounded-xl border border-white/15 bg-white/10 px-4 py-2.5 text-sm text-white placeholder:text-white/40 outline-none transition-colors duration-300 focus:border-[#4DB8FF]"
    : "w-full rounded-xl border border-[#111827]/15 bg-white px-4 py-2.5 text-sm text-[#111827] placeholder:text-slate-400 outline-none transition-colors duration-300 focus:border-[#2E8BFF]";

  return (
    <form onSubmit={submit} data-testid={`newsletter-form-${source}`} noValidate>
      {/* Honeypot invisible */}
      <input type="text" value={hp} onChange={(e) => setHp(e.target.value)} name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" data-testid={`newsletter-honeypot-${source}`} />
      <div className="flex flex-col gap-2.5 sm:flex-row">
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder={t("newsletter.emailPlaceholder")}
          aria-label={t("newsletter.emailLabel")}
          maxLength={200}
          className={inputCls}
          data-testid={`newsletter-email-input-${source}`}
        />
        <button
          type="submit"
          disabled={state === "sending"}
          data-testid={`newsletter-submit-btn-${source}`}
          className={`inline-flex shrink-0 items-center justify-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold transition-transform duration-300 hover:scale-[1.02] active:scale-[0.97] disabled:opacity-60 ${
            dark ? "bg-[#2E8BFF] text-white" : "bg-[#2E8BFF] text-white"
          }`}
        >
          {state === "sending" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          {state === "sending" ? t("newsletter.submitting") : t("newsletter.submit")}
        </button>
      </div>
      <label className={`mt-3 flex items-start gap-2.5 text-xs leading-relaxed ${dark ? "text-white/55" : "text-slate-500"}`} data-testid={`newsletter-consent-label-${source}`}>
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="mt-0.5 h-4 w-4 shrink-0 accent-[#2E8BFF]"
          data-testid={`newsletter-consent-checkbox-${source}`}
        />
        <span>
          {t("newsletter.consent")}{" "}
          <Link to="/politique-de-confidentialite" className={`underline underline-offset-2 ${dark ? "text-white/70 hover:text-white" : "text-[#2E8BFF]"}`} data-testid={`newsletter-privacy-link-${source}`}>
            {t("newsletter.privacy")}
          </Link>
        </span>
      </label>
      {!compact && <p className={`mt-2 text-[11px] ${dark ? "text-white/35" : "text-slate-400"}`}>{t("newsletter.noSpam")}</p>}
      {error && <p className="mt-2 text-xs text-red-500" data-testid={`newsletter-error-${source}`}>{error}</p>}
    </form>
  );
};

/* Modal d'inscription — ouvert depuis « Être informé des prochains programmes » (/opportunites). */
export const NewsletterDialog = ({ open, onOpenChange, source = "opportunites" }) => {
  const { t } = useTranslation();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="newsletter-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-[#111827]">{t("newsletter.title")}</DialogTitle>
          <DialogDescription className="text-sm text-slate-500">{t("newsletter.subtitle")}</DialogDescription>
        </DialogHeader>
        <NewsletterForm source={source} />
      </DialogContent>
    </Dialog>
  );
};
